import { ref } from 'vue';
import { useStore } from 'vuex';
import Swal from 'sweetalert2';

/**
 * Composable que gestiona los archivos del usuario relacionados a una plataforma.
 * Responsabilidad: carga, eliminación y subida de archivos de requisitos.
 */
export function usePlatformFiles() {
  const store = useStore();

  const filesUser = ref([]);

  /**
   * Carga los archivos del usuario para una inscripción específica.
   * Solo carga si el estado de inscripción es diferente a "aprobado para pago" (3).
   *
   * @param {string|number} idUserPlatform
   * @param {number} idTypeStatus
   */
  const loadUserFiles = async (idUserPlatform, idTypeStatus) => {
    if (idTypeStatus == 3) return;

    const params = {
      url: 'user_file_platform/all',
      params: {
        active: 1,
        id_user_platform: idUserPlatform,
        order_by: 'id',
        order_value: 'asc',
      },
    };

    try {
      const response = await store.dispatch('getData', params);
      if (response?.code === 200) {
        filesUser.value = response.data;
      }
    } catch (error) {
      console.error('Error cargando archivos del usuario:', error);
    }
  };

  /**
   * Elimina un archivo del usuario con confirmación SweetAlert2.
   * Llama a onSuccess si la operación es exitosa.
   *
   * @param {string|number} fileId
   * @param {Function} onSuccess
   */
  const deleteFile = async (fileId, onSuccess) => {
    const result = await Swal.fire({
      title: '¿Estás seguro de eliminar este archivo?',
      text: 'No podrás revertir esta acción',
      icon: 'warning',
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonColor: '#048224ff',
      cancelButtonColor: '#abb2ba',
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Sí, eliminar',
    });

    if (!result.value) return;

    const form = {
      url: 'user_file_platform/update',
      id: fileId,
      data: { active: 2 },
    };

    try {
      const response = await store.dispatch('updateData', form);
      if (response?.code === 200) {
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error eliminando archivo:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al eliminar el archivo',
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
    }
  };

  /**
   * Sube los archivos del FilePond para la inscripción del usuario.
   * Llama a onSuccess si la operación es exitosa.
   *
   * @param {Object|null} filePondInstance - Instancia del componente FilePond
   * @param {string|number} idUserPlatform
   * @param {Function} onSuccess
   */
  const uploadFiles = async (filePondInstance, idUserPlatform, onSuccess) => {
    if (!filePondInstance) {
      Swal.fire({
        title: 'Error',
        text: 'No se encontró el componente de carga de archivos',
        icon: 'error',
      });
      return;
    }

    const files = filePondInstance.getFiles?.();

    if (!files || files.length === 0) {
      Swal.fire({
        title: 'Advertencia',
        text: 'Debes adjuntar al menos un archivo',
        icon: 'warning',
        confirmButtonText: 'Entendido',
      });
      return;
    }

    const uploadPromises = files.map((filePondFile) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(filePondFile.file);
        reader.onload = async () => {
          const data = {
            url: 'user_file_platform/save',
            data: {
              id_user_platform: idUserPlatform,
              file: reader.result,
              name: filePondFile.file.name,
            },
          };
          try {
            const response = await store.dispatch('createData', data);
            resolve(response);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = reject;
      });
    });

    try {
      await Promise.all(uploadPromises);
      Swal.fire({
        title: '¡Archivos subidos!',
        text: 'Tus documentos fueron enviados correctamente.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error subiendo archivos:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al subir los archivos',
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
    }
  };

  return {
    filesUser,
    loadUserFiles,
    deleteFile,
    uploadFiles,
  };
}
